import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import { withSessionRecovery } from '@/services/session-recovery'
import type { ApiResponse, PaginatedResponse } from '@/types'

export interface OrgRoleMutationResult {
  message?: string
}

export interface OrgRole {
  id: string
  unique_id: string
  hidden?: boolean
  role_level?: number | null
  role_level_code?: string | null
  role_level_label?: string | null
  active?: boolean | number
  default_role?: boolean
  primary_role?: boolean
  max_assignment_count?: number | null
  level_variant?: number | null
  level_variant_unique_id?: string | null
  level_variant_name?: string | null
  service_instance?: string | null
  service_instance_name?: string | null
  service_variant?: string | number | null
  service_variant_name?: string | null
  scope_type?: 'organization' | 'service_instance' | 'system' | null
  role_group_id?: string
  role_group_name?: string
  post_status?: string | number | null
  post_status_label?: string | null
  status?: string | number | null
  status_label?: string | null
  name: string
  is_system?: boolean
}

export interface OrgRoleGroup {
  id: string | number
  encrypted_id?: string
  title: string
  service_instance_id?: string | null
  service_instance_name?: string | null
  service_variant?: string | number | null
  service_variant_name?: string | null
  scope_type?: 'organization' | 'service_instance' | 'system' | null
  is_system?: boolean
  status?: string | null
}

export interface OrgRolesPageParams {
  page?: number
  pageSize?: number
  search?: string
  serviceInstanceId?: string | null
  serviceVariant?: string | number | null
  active?: boolean | null
}

export interface CreateOrgRolePayload {
  role: string
  roleGroup: string
  serviceInstanceId: string
  serviceVariant?: string | number | null
  applyToOrganization?: boolean
  isSystem?: boolean
  primaryRole?: boolean
  roleLevel?: string | number | null
  maxAssignmentCount?: number | null
  levelVariant: number
}

export interface UpdateOrgRolePayload {
  id: string
  role: string
  serviceInstanceId: string
  serviceVariant?: string | number | null
  applyToOrganization?: boolean
  roleGroup?: string
  primaryRole?: boolean
  roleLevel?: string | number | null
  maxAssignmentCount?: number | null
  levelVariant?: number
}

export interface ToggleOrgRolePayload {
  id: string
  active: boolean
}

export interface CreateOrgRoleGroupPayload {
  title: string
  serviceInstanceId: string
  serviceVariant?: string | number | null
  applyToOrganization?: boolean
}

export interface UpdateOrgRoleGroupPayload {
  id: string
  title: string
  serviceInstanceId: string
  serviceVariant?: string | number | null
  applyToOrganization?: boolean
}

type OrgRoleListPayload =
  | OrgRole[]
  | PaginatedResponse<OrgRole>
  | {
      data?: OrgRole[]
      results?: OrgRole[]
    }

type OrgRoleGroupListPayload =
  | OrgRoleGroup[]
  | PaginatedResponse<OrgRoleGroup>
  | {
      data?: OrgRoleGroup[]
      results?: OrgRoleGroup[]
    }

const emptyPage = <T>(): PaginatedResponse<T> => ({
  count: 0,
  next: null,
  previous: null,
  results: [],
})

const extractPayload = <T>(payload: ApiResponse<T> | T): T => (
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? payload.data
    : payload
) as T

const normalizePaginatedResponse = <T>(
  payload: T[] | PaginatedResponse<T> | { data?: T[]; results?: T[] } | undefined,
): PaginatedResponse<T> => {
  if (!payload) {
    return emptyPage<T>()
  }

  if (Array.isArray(payload)) {
    return {
      count: payload.length,
      next: null,
      previous: null,
      results: payload,
    }
  }

  if ('count' in payload && Array.isArray(payload.results)) {
    return payload
  }

  const results = Array.isArray(payload.results)
    ? payload.results
    : 'data' in payload && Array.isArray(payload.data)
      ? payload.data
      : []

  return {
    count: results.length,
    next: null,
    previous: null,
    results,
  }
}

const normalizeOrgRoles = (payload?: OrgRoleListPayload): OrgRole[] => {
  if (!payload) {
    return []
  }

  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload.results)) {
    return payload.results
  }

  if ('data' in payload && Array.isArray(payload.data)) {
    return payload.data
  }

  return []
}

const normalizeOrgRoleGroups = (payload?: OrgRoleGroupListPayload): OrgRoleGroup[] => {
  if (!payload) {
    return []
  }

  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload.results)) {
    return payload.results
  }

  if ('data' in payload && Array.isArray(payload.data)) {
    return payload.data
  }

  return []
}

const fetchOrgRolesList = async ({
  serviceInstanceId,
  serviceVariant,
  includeHidden,
}: {
  serviceInstanceId?: string | null
  serviceVariant?: string | number | null
  includeHidden?: boolean
}): Promise<OrgRole[]> => {
  const response = await api.get<ApiResponse<OrgRoleListPayload> | OrgRoleListPayload>(API_PATHS.orgRoles.list, {
    params: {
      service_instance_id: serviceInstanceId || undefined,
      service_variant: serviceVariant || undefined,
      include_hidden: includeHidden ? 1 : undefined,
      page_size: 100,
    },
  })

  const payload = extractPayload(response.data)
  return normalizeOrgRoles(payload)
}

const fetchOrgRolesPage = async ({
  page,
  pageSize,
  search,
  serviceInstanceId,
  serviceVariant,
  active,
}: OrgRolesPageParams): Promise<PaginatedResponse<OrgRole>> => {
  const response = await api.get<ApiResponse<OrgRoleListPayload> | OrgRoleListPayload>(API_PATHS.orgRoles.list, {
    params: {
      service_instance_id: serviceInstanceId || undefined,
      service_variant: serviceVariant || undefined,
      page,
      page_size: pageSize,
      search: search || undefined,
      active: typeof active === 'boolean' ? (active ? 1 : 0) : undefined,
    },
  })

  return normalizePaginatedResponse(extractPayload(response.data))
}

export const orgRolesApi = {
  list: async ({ serviceInstanceId, serviceVariant, includeHidden }: { search?: string; serviceInstanceId?: string | null; serviceVariant?: string | number | null; includeHidden?: boolean }): Promise<OrgRole[]> => {
    return withSessionRecovery(() => fetchOrgRolesList({ serviceInstanceId, serviceVariant, includeHidden }))
  },

  getPage: async ({ page = 1, pageSize = 10, search, serviceInstanceId, serviceVariant, active = null }: OrgRolesPageParams): Promise<PaginatedResponse<OrgRole>> => {
    return withSessionRecovery(() => fetchOrgRolesPage({ page, pageSize, search, serviceInstanceId, serviceVariant, active }))
  },

  get: async (id: string | number): Promise<OrgRole> => withSessionRecovery(async () => {
    const response = await api.get<ApiResponse<OrgRole> | OrgRole>(API_PATHS.orgRoles.get(id))
    return extractPayload(response.data)
  }),

  listGroups: async ({ serviceInstanceId, serviceVariant }: { serviceInstanceId?: string | null; serviceVariant?: string | number | null }): Promise<OrgRoleGroup[]> => {
    const response = await api.get<ApiResponse<OrgRoleGroupListPayload> | OrgRoleGroupListPayload>(API_PATHS.orgRoles.groupsList, {
      params: {
        org_instance: serviceInstanceId || undefined,
        service_variant: serviceVariant || undefined,
        page_size: 100,
      },
    })

    return normalizeOrgRoleGroups(extractPayload(response.data))
  },

  createGroup: async ({ title, serviceInstanceId, serviceVariant, applyToOrganization }: CreateOrgRoleGroupPayload): Promise<OrgRoleGroup> => {
    const response = await api.post<ApiResponse<OrgRoleGroup> | OrgRoleGroup>(API_PATHS.orgRoles.groupsCreate, {
      title,
      org_instance: serviceInstanceId,
      service_variant: serviceVariant ?? undefined,
      apply_to_organization: applyToOrganization ? 1 : 0,
    })

    return extractPayload(response.data)
  },

  updateGroup: async ({ id, title, serviceInstanceId, serviceVariant, applyToOrganization }: UpdateOrgRoleGroupPayload): Promise<OrgRoleGroup> => {
    const response = await api.patch<ApiResponse<OrgRoleGroup> | OrgRoleGroup>(API_PATHS.orgRoles.groupsUpdate(id), {
      title,
      org_instance: serviceInstanceId,
      service_variant: serviceVariant ?? undefined,
      apply_to_organization: applyToOrganization ? 1 : 0,
    })

    return extractPayload(response.data)
  },

  removeGroup: async (id: string): Promise<OrgRoleMutationResult> => {
    const response = await api.delete<ApiResponse<unknown> | OrgRoleMutationResult>(API_PATHS.orgRoles.groupsDelete(id))
    const payload = response.data

    if (typeof payload === 'object' && payload !== null && 'message' in payload) {
      return { message: payload.message as string | undefined }
    }

    return {}
  },

  create: async ({ role, roleGroup, serviceInstanceId, serviceVariant, applyToOrganization, isSystem, primaryRole, roleLevel, maxAssignmentCount, levelVariant }: CreateOrgRolePayload): Promise<OrgRole> => {
    const response = await api.post<ApiResponse<OrgRole> | OrgRole>(API_PATHS.orgRoles.create, {
      role,
      role_group: roleGroup,
      service_instance: serviceInstanceId,
      service_variant: serviceVariant ?? undefined,
      apply_to_organization: applyToOrganization ? 1 : 0,
      is_system: isSystem ? 1 : 0,
      primary_role: primaryRole ? 1 : 0,
      role_level: roleLevel ?? undefined,
      max_assignment_count: maxAssignmentCount ?? undefined,
      level_variant: levelVariant,
    })

    return extractPayload(response.data)
  },

  update: async ({ id, role, roleGroup, serviceInstanceId, serviceVariant, applyToOrganization, primaryRole, roleLevel, maxAssignmentCount, levelVariant }: UpdateOrgRolePayload): Promise<OrgRole> => {
    const response = await api.patch<ApiResponse<OrgRole> | OrgRole>(API_PATHS.orgRoles.update(id), {
      role,
      service_instance: serviceInstanceId,
      service_variant: serviceVariant ?? undefined,
      apply_to_organization: applyToOrganization ? 1 : 0,
      role_group: roleGroup || undefined,
      primary_role: primaryRole ? 1 : 0,
      role_level: roleLevel ?? undefined,
      max_assignment_count: maxAssignmentCount ?? undefined,
      ...(levelVariant !== undefined ? { level_variant: levelVariant } : {}),
    })

    return extractPayload(response.data)
  },

  setActiveState: async ({ id, active }: ToggleOrgRolePayload): Promise<void> => {
    await api.patch(active ? API_PATHS.orgRoles.activate(id) : API_PATHS.orgRoles.deactivate(id))
  },

  remove: async (id: string): Promise<OrgRoleMutationResult> => {
    const response = await api.delete<ApiResponse<unknown> | OrgRoleMutationResult>(API_PATHS.orgRoles.delete(id))
    const payload = response.data

    if (typeof payload === 'object' && payload !== null && 'message' in payload) {
      return { message: payload.message as string | undefined }
    }

    return {}
  },
}